import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ChevronDownIcon, 
  ChevronUpIcon, 
  ArrowDownTrayIcon,
  ArrowLeftIcon,
  ClockIcon,
  DocumentTextIcon
} from '@heroicons/react/24/outline';
import axios from 'axios';
import AdminLogin from '../components/AdminLogin';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';
const ADMIN_PASSWORD = process.env.REACT_APP_ADMIN_PASSWORD || 'admin123';

interface Version {
  version_id: string;
  uploaded_at: string;
  filename?: string;
  file_size?: number;
  files?: { filename: string; file_size: number }[];
  total_size?: number;
}

interface Presentation {
  id: string;
  speaker_name: string;
  speaker_email: string;
  presentation_title: string;
  upload_link: string;
  versions: Version[];
  created_at: string;
  updated_at: string;
  published: boolean;
}

const AdminPage: React.FC = () => {
  const navigate = useNavigate();
  const [presentations, setPresentations] = useState<Presentation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authError, setAuthError] = useState('');
  const [updatingId, setUpdatingId] = useState<string | null>(null);

  useEffect(() => {
    if (isAuthenticated) {
      fetchPresentations();
    }
  }, [isAuthenticated]);

  const handleLogin = (password: string) => {
    if (password === ADMIN_PASSWORD) {
      setIsAuthenticated(true);
      setAuthError('');
    } else {
      setAuthError('Incorrect password. Please try again.');
    }
  };

  const togglePublish = async (presentation: Presentation) => {
    const newValue = !presentation.published;
    setUpdatingId(presentation.id);
    try {
      const response = await axios.patch(
        `${API_BASE_URL}/api/presentations/${presentation.id}/publish`,
        { published: newValue }
      );
      const updated = response.data as Presentation;
      setPresentations((prev) =>
        prev.map((p) => (p.id === presentation.id ? { ...p, published: updated.published, updated_at: updated.updated_at } : p))
      );
      setError(null);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to update publish status');
    } finally {
      setUpdatingId(null);
    }
  };

  if (!isAuthenticated) {
    return (
      <>
        <AdminLogin onLogin={handleLogin} />
        {authError && (
          <div className="fixed top-4 right-4 bg-red-50 border-l-4 border-red-400 p-4 rounded shadow-lg">
            <p className="text-sm text-red-700">{authError}</p>
          </div>
        )}
      </>
    );
  }

  const fetchPresentations = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/presentations`);
      setPresentations(response.data);
      setLoading(false);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to load presentations');
      setLoading(false);
    }
  };

  const toggleExpanded = (id: string) => {
    const newExpanded = new Set(expandedIds);
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
    }
    setExpandedIds(newExpanded);
  };

  const downloadVersion = (presentationId: string, versionId: string, filename?: string) => {
    const url = filename
      ? `${API_BASE_URL}/api/download/${presentationId}/${versionId}/${encodeURIComponent(filename)}`
      : `${API_BASE_URL}/api/download/${presentationId}/${versionId}`;
    const link = document.createElement('a');
    link.href = url;
    if (filename) link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
  };

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#00529B]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-12 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/')}
            className="flex items-center text-[#00529B] hover:text-[#003D74] mb-4"
          >
            <ArrowLeftIcon className="h-5 w-5 mr-2" />
            Back to Upload
          </button>
          
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-4xl font-bold mb-2 bg-gradient-to-r from-[#00529B] to-[#E31D3C] bg-clip-text text-transparent">
                Admin Dashboard
              </h1>
              <p className="text-gray-600">
                Manage all submitted presentations
              </p>
            </div>
            <div className="text-right">
              <p className="text-2xl font-bold text-[#00529B]">{presentations.length}</p>
              <p className="text-sm text-gray-600">Total Presentations</p>
            </div>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border-l-4 border-red-400 p-4 rounded mb-6">
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}

        {/* Presentations List */}
        {presentations.length === 0 ? (
          <div className="bg-white rounded-2xl shadow-lg p-12 text-center">
            <DocumentTextIcon className="mx-auto h-16 w-16 text-gray-400 mb-4" />
            <h3 className="text-xl font-semibold text-gray-700 mb-2">No Presentations Yet</h3>
            <p className="text-gray-500">Presentations will appear here once speakers upload them.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {presentations.map((presentation) => {
              const isExpanded = expandedIds.has(presentation.id);
              const latestVersion = presentation.versions[presentation.versions.length - 1];
              
              return (
                <div key={presentation.id} className="bg-white rounded-2xl shadow-lg overflow-hidden">
                  {/* Main Info */}
                  <div className="p-6">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h3 className="text-xl font-bold text-gray-900 mb-2">
                          {presentation.presentation_title}
                        </h3>
                        <div className="space-y-1 text-sm text-gray-600">
                          <p>
                            <span className="font-semibold">Speaker:</span> {presentation.speaker_name}
                          </p>
                          <p>
                            <span className="font-semibold">Email:</span> {presentation.speaker_email}
                          </p>
                          <p className="flex items-center">
                            <ClockIcon className="h-4 w-4 mr-1" />
                            <span className="font-semibold">Last Updated:</span> {formatDate(presentation.updated_at)}
                          </p>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-4 ml-4">
                        <span className="px-3 py-1 bg-[#00529B] text-white text-sm font-semibold rounded-full">
                          {presentation.versions.length} version{presentation.versions.length !== 1 ? 's' : ''}
                        </span>
                        <label className="flex items-center gap-2 text-sm text-gray-700">
                          <input
                            type="checkbox"
                            checked={presentation.published}
                            onChange={() => togglePublish(presentation)}
                            disabled={updatingId === presentation.id}
                            className="h-4 w-4 text-[#00529B] border-gray-300 rounded focus:ring-[#00529B]"
                          />
                          <span>
                            {presentation.published ? 'Published on website' : 'Not published'}
                          </span>
                        </label>
                      </div>
                    </div>

                    {/* Latest Version */}
                    <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                      <p className="text-sm font-semibold text-gray-700 mb-3">Latest Version</p>
                      {latestVersion.files && latestVersion.files.length > 0 ? (
                        <div className="space-y-2">
                          {latestVersion.files.map((f) => (
                            <div key={f.filename} className="flex items-center justify-between">
                              <div className="text-sm text-gray-700">{f.filename}</div>
                              <div className="flex items-center gap-4">
                                <span className="text-xs text-gray-500">{formatFileSize(f.file_size)}</span>
                                <button
                                  onClick={() => downloadVersion(presentation.id, latestVersion.version_id, f.filename)}
                                  className="btn-primary flex items-center gap-2"
                                >
                                  <ArrowDownTrayIcon className="h-5 w-5" />
                                  Download
                                </button>
                              </div>
                            </div>
                          ))}
                          <p className="text-xs text-gray-500 mt-2">
                            {formatDate(latestVersion.uploaded_at)}{latestVersion.total_size ? ` • Total ${formatFileSize(latestVersion.total_size)}` : ''}
                          </p>
                        </div>
                      ) : (
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm text-gray-600">{latestVersion.filename}</p>
                            <p className="text-xs text-gray-500 mt-1">
                              {latestVersion.file_size ? formatFileSize(latestVersion.file_size) : ''} • {formatDate(latestVersion.uploaded_at)}
                            </p>
                          </div>
                          <button
                            onClick={() => downloadVersion(presentation.id, latestVersion.version_id, latestVersion.filename)}
                            className="btn-primary flex items-center gap-2"
                          >
                            <ArrowDownTrayIcon className="h-5 w-5" />
                            Download
                          </button>
                        </div>
                      )}
                    </div>

                    {/* Toggle Previous Versions */}
                    {presentation.versions.length > 1 && (
                      <button
                        onClick={() => toggleExpanded(presentation.id)}
                        className="mt-4 flex items-center text-[#00529B] hover:text-[#003D74] text-sm font-semibold"
                      >
                        {isExpanded ? (
                          <>
                            <ChevronUpIcon className="h-5 w-5 mr-1" />
                            Hide Previous Versions
                          </>
                        ) : (
                          <>
                            <ChevronDownIcon className="h-5 w-5 mr-1" />
                            Show Previous Versions ({presentation.versions.length - 1})
                          </>
                        )}
                      </button>
                    )}
                  </div>

                  {/* Previous Versions */}
                  {isExpanded && presentation.versions.length > 1 && (
                    <div className="border-t border-gray-200 bg-gray-50 p-6">
                      <h4 className="text-sm font-semibold text-gray-700 mb-3">Previous Versions</h4>
                      <div className="space-y-3">
                        {presentation.versions.slice(0, -1).reverse().map((version, index) => (
                          <div
                            key={version.version_id}
                            className="flex items-start justify-between p-3 bg-white rounded-lg"
                          >
                            <div className="flex-1">
                              <p className="text-sm font-medium text-gray-700">
                                Version {presentation.versions.length - index - 1}
                              </p>
                              {version.files && version.files.length > 0 ? (
                                <div className="mt-1 space-y-1">
                                  {version.files.map((f) => (
                                    <div key={f.filename} className="flex items-center justify-between">
                                      <span className="text-sm text-gray-600">{f.filename}</span>
                                      <span className="text-xs text-gray-500">{formatFileSize(f.file_size)}</span>
                                    </div>
                                  ))}
                                  <p className="text-xs text-gray-500 mt-1">{formatDate(version.uploaded_at)}</p>
                                </div>
                              ) : (
                                <>
                                  <p className="text-sm text-gray-600">{version.filename}</p>
                                  <p className="text-xs text-gray-500 mt-1">
                                    {version.file_size ? formatFileSize(version.file_size) : ''} • {formatDate(version.uploaded_at)}
                                  </p>
                                </>
                              )}
                            </div>
                            <div className="ml-4">
                              {version.files && version.files.length > 0 ? (
                                <div className="flex flex-col gap-2">
                                  {version.files.map((f) => (
                                    <button
                                      key={f.filename}
                                      onClick={() => downloadVersion(presentation.id, version.version_id, f.filename)}
                                      className="btn-outline text-sm py-2 px-4 flex items-center gap-2"
                                    >
                                      <ArrowDownTrayIcon className="h-4 w-4" />
                                      Download {f.filename}
                                    </button>
                                  ))}
                                </div>
                              ) : (
                                <button
                                  onClick={() => downloadVersion(presentation.id, version.version_id, version.filename)}
                                  className="btn-outline text-sm py-2 px-4 flex items-center gap-2"
                                >
                                  <ArrowDownTrayIcon className="h-4 w-4" />
                                  Download
                                </button>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminPage;
